/**
 * API Service for Node.js Backend
 * Handles all HTTP requests to the backend API
 */

// API Base URL - change this for production
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Token storage keys
const ACCESS_TOKEN_KEY = 'adminToken';
const REFRESH_TOKEN_KEY = 'refreshToken';

/**
 * Get stored access token
 */
export const getAccessToken = () => localStorage.getItem(ACCESS_TOKEN_KEY);

/**
 * Get stored refresh token
 */
export const getRefreshToken = () => localStorage.getItem(REFRESH_TOKEN_KEY);

/**
 * Store tokens
 */
export const setTokens = (tokens) => {
  // Support both camelCase (Node.js) and snake_case (PHP) formats
  const accessToken = tokens.accessToken || tokens.access_token;
  const refreshToken = tokens.refreshToken || tokens.refresh_token;

  if (accessToken) {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  }
  if (refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  }
};

/**
 * Clear tokens (logout)
 */
export const clearTokens = () => {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem('adminLoggedIn');
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = () => {
  const token = getAccessToken();
  if (!token) return false;

  // Basic JWT expiry check (decode without verification)
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp > Date.now() / 1000;
  } catch {
    return false;
  }
};

/**
 * Make API request with automatic token handling
 */
const apiRequest = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;

  const headers = {
    ...options.headers,
  };

  // Add auth token if available (except for FormData)
  const token = getAccessToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Add JSON content type for non-FormData requests
  if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  const config = {
    ...options,
    headers,
  };

  try {
    let response = await fetch(url, config);

    // Handle 401 - try to refresh token
    if (response.status === 401 && getRefreshToken()) {
      const refreshed = await refreshAccessToken();
      if (refreshed) {
        // Retry request with new token
        headers['Authorization'] = `Bearer ${getAccessToken()}`;
        response = await fetch(url, { ...config, headers });
      } else {
        clearTokens();
        throw new Error('Session expired. Please login again.');
      }
    }

    // Check if response is JSON
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      // API returned non-JSON (likely HTML error page)
      const text = await response.text();
      console.error('API returned non-JSON response:', text.substring(0, 500));
      throw new Error('Server configuration error. Please check API setup.');
    }

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || data.error || 'Request failed');
    }

    return data;
  } catch (error) {
    if (error.message === 'Failed to fetch') {
      throw new Error('Network error. Please check your connection.');
    }
    // Re-throw with more context if it's a generic error
    if (error instanceof SyntaxError) {
      throw new Error('Invalid server response. Please check API configuration.');
    }
    throw error;
  }
};

/**
 * Refresh access token
 */
const refreshAccessToken = async () => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return false;

  try {
    const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });

    if (response.ok) {
      const data = await response.json();
      // Handle both response formats
      const tokens = data.data.tokens || { accessToken: data.data.accessToken };
      setTokens(tokens);
      return true;
    }
  } catch {
    // Refresh failed
  }

  return false;
};

// ============================================
// AUTH API
// ============================================

export const authApi = {
  /**
   * Login admin
   */
  login: async (email, password) => {
    const response = await apiRequest('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    if (response.success && response.data) {
      // Handle both token response formats
      const tokens = response.data.tokens || {
        accessToken: response.data.accessToken,
        refreshToken: response.data.refreshToken
      };
      setTokens(tokens);
      localStorage.setItem('adminLoggedIn', 'true');
    }
    return response;
  },

  /**
   * Logout
   */
  logout: async () => {
    const refreshToken = getRefreshToken();
    try {
      await apiRequest('/auth/logout', {
        method: 'POST',
        body: JSON.stringify({ refreshToken }),
      });
    } catch {
      // Ignore errors during logout
    }
    clearTokens();
  },

  /**
   * Get current admin info
   */
  me: () => apiRequest('/auth/me'),

  /**
   * Change password
   */
  changePassword: (currentPassword, newPassword) =>
    apiRequest('/auth/password', {
      method: 'PUT',
      body: JSON.stringify({
        currentPassword,
        newPassword,
      }),
    }),
};

// ============================================
// BLOGS API
// ============================================

export const blogsApi = {
  /**
   * Get all categories (public)
   */
  getCategories: () => apiRequest('/blogs/categories'),

  /**
   * Get published blogs (public)
   */
  getPublished: (page = 1, perPage = 10, category = '') => {
    let url = `/blogs?page=${page}&per_page=${perPage}`;
    if (category && category !== 'all') url += `&category=${encodeURIComponent(category)}`;
    return apiRequest(url);
  },

  /**
   * Get single blog by slug (public)
   */
  getBySlug: (slug) => apiRequest(`/blogs/${slug}`),

  /**
   * Get all blogs for admin (including drafts)
   */
  getAll: (page = 1, perPage = 10, status = '', search = '', category = '') => {
    let url = `/blogs/admin/list?page=${page}&per_page=${perPage}`;
    if (status) url += `&status=${status}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    if (category && category !== 'all') url += `&category=${encodeURIComponent(category)}`;
    return apiRequest(url);
  },

  /**
   * Get single blog by ID (admin)
   */
  getById: (id) => apiRequest(`/blogs/admin/${id}`),

  /**
   * Create new blog
   */
  create: (blogData) =>
    apiRequest('/blogs', {
      method: 'POST',
      body: JSON.stringify(blogData),
    }),

  /**
   * Update blog
   */
  update: (id, blogData) =>
    apiRequest(`/blogs/${id}`, {
      method: 'PUT',
      body: JSON.stringify(blogData),
    }),

  /**
   * Delete blog
   */
  delete: (id) =>
    apiRequest(`/blogs/${id}`, {
      method: 'DELETE',
    }),

  /**
   * Upload image
   */
  uploadImage: async (file) => {
    const formData = new FormData();
    formData.append('image', file);

    return apiRequest('/blogs/upload-image', {
      method: 'POST',
      body: formData,
    });
  },

  /**
   * Generate blog content using AI
   */
  generate: (topic, category = '', tone = 'professional', length = 'medium') =>
    apiRequest('/blogs/generate', {
      method: 'POST',
      body: JSON.stringify({ topic, category, tone, length }),
    }),
};

// ============================================
// CONTACTS API
// ============================================

export const contactsApi = {
  /**
   * Submit contact form (public)
   */
  submit: (contactData) =>
    apiRequest('/contacts', {
      method: 'POST',
      body: JSON.stringify(contactData),
    }),

  /**
   * Get all contacts (admin)
   */
  getAll: (page = 1, perPage = 10, isRead = '', search = '') => {
    let url = `/contacts?page=${page}&per_page=${perPage}`;
    if (isRead !== '') url += `&is_read=${isRead}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    return apiRequest(url);
  },

  /**
   * Get single contact
   */
  getById: (id) => apiRequest(`/contacts/${id}`),

  /**
   * Mark as read
   */
  markRead: (id) =>
    apiRequest(`/contacts/${id}/read`, {
      method: 'PUT',
    }),

  /**
   * Mark as unread
   */
  markUnread: (id) =>
    apiRequest(`/contacts/${id}/unread`, {
      method: 'PUT',
    }),

  /**
   * Delete contact
   */
  delete: (id) =>
    apiRequest(`/contacts/${id}`, {
      method: 'DELETE',
    }),

  /**
   * Get contact stats
   */
  getStats: () => apiRequest('/contacts/stats'),
};

// ============================================
// USERS API (Admin Management)
// ============================================

export const usersApi = {
  /**
   * Get all users (admin)
   */
  getAll: (page = 1, perPage = 10, status = '', search = '') => {
    let url = `/users?page=${page}&per_page=${perPage}`;
    if (status) url += `&status=${status}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    return apiRequest(url);
  },

  /**
   * Get single user
   */
  getById: (id) => apiRequest(`/users/${id}`),

  /**
   * Create new user
   */
  create: (userData) =>
    apiRequest('/users', {
      method: 'POST',
      body: JSON.stringify(userData),
    }),

  /**
   * Update user
   */
  update: (id, userData) =>
    apiRequest(`/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(userData),
    }),

  /**
   * Toggle user active status
   */
  toggleStatus: (id) =>
    apiRequest(`/users/${id}/toggle`, {
      method: 'PUT',
    }),

  /**
   * Delete user
   */
  delete: (id) =>
    apiRequest(`/users/${id}`, {
      method: 'DELETE',
    }),

  /**
   * Get user stats
   */
  getStats: () => apiRequest('/users/stats'),
};

// ============================================
// API KEYS API
// ============================================

export const apiKeysApi = {
  /**
   * Get all API keys for current admin
   */
  getAll: () => apiRequest('/api-keys'),

  /**
   * Create new API key
   */
  create: (data) =>
    apiRequest('/api-keys', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  /**
   * Update API key
   */
  update: (id, data) =>
    apiRequest(`/api-keys/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  /**
   * Delete/revoke API key
   */
  delete: (id) =>
    apiRequest(`/api-keys/${id}`, {
      method: 'DELETE',
    }),
};

// ============================================
// LINKEDIN API
// ============================================

export const linkedinApi = {
  /**
   * Get LinkedIn OAuth URL
   */
  getAuthUrl: () => apiRequest('/linkedin/auth-url'),

  /**
   * Check LinkedIn connection status
   */
  getStatus: () => apiRequest('/linkedin/status'),

  /**
   * Generate LinkedIn post preview content
   */
  generatePreview: (blogId) =>
    apiRequest('/linkedin/generate-preview', {
      method: 'POST',
      body: JSON.stringify({ blogId }),
    }),

  /**
   * Post to LinkedIn
   */
  post: (blogId, content = '', includeLink = true, useAI = false) =>
    apiRequest('/linkedin/post', {
      method: 'POST',
      body: JSON.stringify({ blogId, content, includeLink, useAI }),
    }),

  /**
   * Disconnect LinkedIn
   */
  disconnect: () =>
    apiRequest('/linkedin/disconnect', {
      method: 'DELETE',
    }),
};

const apiService = {
  auth: authApi,
  blogs: blogsApi,
  contacts: contactsApi,
  users: usersApi,
  apiKeys: apiKeysApi,
  linkedin: linkedinApi,
};

export default apiService;
